import { isOwnAppUrl } from './appUrl';

export const DESKTOP_PROTOCOL = 'eleplan';
export const DESKTOP_HANDOFF_PATH = '/open-app';

export const APP_DOWNLOAD_LINKS = {
  desktop: 'https://eleplan.com/download',
  mobile: 'https://eleplan.com/get',
} as const;

export interface ProjectChatDeepLink {
  projectId: number;
  accountId?: string;
  conversationId?: string;
}

function normalizeProjectId(value: number | string | null | undefined): number | null {
  const raw = String(value ?? '').trim();
  if (!/^\d+$/.test(raw)) return null;
  const projectId = Number(raw);
  return Number.isSafeInteger(projectId) && projectId > 0 ? projectId : null;
}

function normalizeConversationId(value: string | null | undefined): string | undefined {
  const conversationId = String(value ?? '').trim();
  // Current conversation ids are UUIDs; legacy single-query threads use
  // `single-<id>`. Keep the handoff deliberately narrower than arbitrary URL
  // text so a copied/tampered handoff cannot smuggle control characters or an
  // unbounded query string into the desktop protocol invocation.
  return /^[A-Za-z0-9._~-]{1,256}$/.test(conversationId) ? conversationId : undefined;
}

function hasConversationId(value: string | null | undefined): boolean {
  return value != null && String(value).trim().length > 0;
}

function normalizeAccountId(value: string | null | undefined): string | null {
  const accountId = String(value ?? '').trim();
  // Cognito-backed legacy ids include provider prefixes such as Google_ and
  // SignInWithApple_; keep the full RFC3986-unreserved set, but no URL/query
  // separators or whitespace.
  return /^[A-Za-z0-9._~-]{1,255}$/.test(accountId) ? accountId : null;
}

export function buildDesktopProtocolUrl(
  projectIdInput: number | string,
  accountIdInput: string,
  conversationIdInput?: string | null,
): string | null {
  const projectId = normalizeProjectId(projectIdInput);
  const accountId = normalizeAccountId(accountIdInput);
  if (!projectId || !accountId) return null;
  const conversationId = normalizeConversationId(conversationIdInput);
  if (hasConversationId(conversationIdInput) && !conversationId) return null;
  const query = new URLSearchParams({ account_id: accountId });
  if (conversationId) query.set('conversation_id', conversationId);
  return `${DESKTOP_PROTOCOL}://project/${projectId}/chat?${query.toString()}`;
}

export function buildDesktopHandoffPath(
  projectIdInput: number | string,
  accountIdInput: string,
  conversationIdInput?: string | null,
): string | null {
  const projectId = normalizeProjectId(projectIdInput);
  const accountId = normalizeAccountId(accountIdInput);
  if (!projectId || !accountId) return null;
  const conversationId = normalizeConversationId(conversationIdInput);
  if (hasConversationId(conversationIdInput) && !conversationId) return null;
  const query = new URLSearchParams({
    project_id: String(projectId),
    account_id: accountId,
  });
  if (conversationId) query.set('conversation_id', conversationId);
  return `${DESKTOP_HANDOFF_PATH}?${query.toString()}`;
}

export function parseDesktopHandoffSearch(search: string): ProjectChatDeepLink | null {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const keys = Array.from(params.keys());
  if (keys.some((key) => !['project_id', 'account_id', 'conversation_id'].includes(key))) return null;
  if (params.getAll('project_id').length !== 1 || params.getAll('account_id').length !== 1) return null;
  if (params.getAll('conversation_id').length > 1) return null;
  const projectId = normalizeProjectId(params.get('project_id'));
  const accountId = normalizeAccountId(params.get('account_id'));
  if (!projectId || !accountId) return null;
  const conversationId = normalizeConversationId(params.get('conversation_id'));
  if (params.has('conversation_id') && !conversationId) return null;
  return conversationId
    ? { projectId, accountId, conversationId }
    : { projectId, accountId };
}

/**
 * Recognise the chat target carried by an Eleplan universal/custom-scheme URL.
 * This is shared by native email links and the browser -> Electron handoff, so
 * both warm and cold launches navigate through one parser.
 */
export function extractProjectChatDeepLink(
  url: string | null | undefined,
  currentWebOrigin: string | null | undefined = typeof window !== 'undefined'
    ? window.location?.origin
    : undefined,
): ProjectChatDeepLink | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const isDesktopScheme = parsed.protocol.toLowerCase() === `${DESKTOP_PROTOCOL}:`;
  const customScheme = isDesktopScheme || parsed.protocol.toLowerCase() === 'com.eleplan.app:';
  let matchesCurrentWebOrigin = false;
  if (currentWebOrigin) {
    try {
      matchesCurrentWebOrigin = parsed.origin === new URL(currentWebOrigin).origin;
    } catch {
      matchesCurrentWebOrigin = false;
    }
  }
  // Production/stage universal links are checked against the configured app
  // base. Web builds can also legitimately run at a different current origin
  // (local e2e, preview hosts, or a customer deployment), so accept that exact
  // origin without weakening the rejection of arbitrary external URLs.
  if (!customScheme && !isOwnAppUrl(url) && !matchesCurrentWebOrigin) return null;

  // In a custom URL (`eleplan://project/42/chat`) "project" is the host;
  // in the universal URL it is the first path segment. Require the entire
  // route shape so a hostile host/path containing this substring cannot pass.
  const path = customScheme
    ? (parsed.hostname.toLowerCase() === 'project' ? parsed.pathname.match(/^\/(\d+)\/chat\/?$/i) : null)
    : parsed.pathname.match(/^\/project\/(\d+)\/chat\/?$/i);
  const projectId = normalizeProjectId(path?.[1]);
  if (!projectId) return null;

  const accountId = normalizeAccountId(parsed.searchParams.get('account_id'));
  if (isDesktopScheme && !accountId) return null;
  if (parsed.searchParams.has('account_id') && !accountId) return null;
  if (isDesktopScheme) {
    const keys = Array.from(parsed.searchParams.keys());
    if (keys.some((key) => key !== 'account_id' && key !== 'conversation_id')) return null;
    if (parsed.searchParams.getAll('account_id').length !== 1) return null;
    if (parsed.searchParams.getAll('conversation_id').length > 1) return null;
  }
  const conversationId = normalizeConversationId(parsed.searchParams.get('conversation_id'));
  if (parsed.searchParams.has('conversation_id') && !conversationId) return null;
  return {
    projectId,
    ...(accountId ? { accountId } : {}),
    ...(conversationId ? { conversationId } : {}),
  };
}

/**
 * Recognise the "For your review" list (AgentSuggestions) named by an Eleplan
 * universal/custom-scheme URL: /project/<id>/suggestions, the link an AI
 * assistant hands back when it queues items for review. The web linking config
 * already opens it in a browser; native routes URLs by hand in RootNavigator,
 * which needs this parser. Origin-gated like extractProjectChatDeepLink and
 * requires the whole route, so neither a hostile host nor the path tucked into
 * a query string or fragment can pass. The "View" link of a care-agent nudge
 * adds ?note=<id>, the finding it was about, returned as careNoteId. Any other
 * query parameter is ignored: assistants append their own tracking parameters
 * to links.
 */
export function extractProjectSuggestionsDeepLink(
  url: string | null | undefined,
): { projectId: number; careNoteId?: number } | null {
  if (!url || !isOwnAppUrl(url)) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  // In `com.eleplan.app://project/42/suggestions` "project" is the host; in the
  // universal URL it is the first path segment.
  const path = parsed.protocol.toLowerCase() === 'com.eleplan.app:'
    ? (parsed.hostname.toLowerCase() === 'project' ? parsed.pathname.match(/^\/(\d+)\/suggestions\/?$/i) : null)
    : parsed.pathname.match(/^\/project\/(\d+)\/suggestions\/?$/i);
  const projectId = normalizeProjectId(path?.[1]);
  if (!projectId) return null;

  // A finding's id is a positive integer like a plan's. A missing or malformed
  // `note` still opens the list; so does a repeated one, which is ambiguous, so
  // no finding is guessed.
  const notes = parsed.searchParams.getAll('note');
  const careNoteId = notes.length === 1 ? normalizeProjectId(notes[0]) : null;
  return careNoteId ? { projectId, careNoteId } : { projectId };
}

/** Current Notes links and saved pre-split Files links. Only app-owned URLs qualify. */
export function extractProjectNotesDeepLink(url: string | null | undefined): { projectId: number; openNoteId?: number } | null {
  if (!url || !isOwnAppUrl(url)) return null;
  let parsed: URL;
  try { parsed = new URL(url); } catch { return null; }
  const route = parsed.protocol.toLowerCase() === 'com.eleplan.app:'
    ? (parsed.hostname.toLowerCase() === 'project' ? parsed.pathname.match(/^\/(\d+)\/(notes|files)\/?$/i) : null)
    : parsed.pathname.match(/^\/project\/(\d+)\/(notes|files)\/?$/i);
  const projectId = normalizeProjectId(route?.[1]);
  if (!projectId) return null;
  const ids = parsed.searchParams.getAll('openNoteId');
  const tabs = parsed.searchParams.getAll('tab');
  const initialTabs = parsed.searchParams.getAll('initialTab');
  const openNoteId = ids.length === 1 ? normalizeProjectId(ids[0]) : null;
  if (route?.[2].toLowerCase() !== 'notes' && !openNoteId
    && !(tabs.length === 1 && tabs[0] === 'notes') && !(initialTabs.length === 1 && initialTabs[0] === 'notes')) return null;
  return { projectId, ...(openNoteId ? { openNoteId } : {}) };
}

/**
 * Recognise the plan's Files screen named by an Eleplan universal/custom-scheme
 * URL: /project/<id>/files[?openUpload=1]. Claude and Perplexity can't hand a
 * file over, so the connector gives them this link: it opens the plan's normal
 * upload sheet and the person chooses the file themselves. The web linking
 * config already opens it in a browser; native routes URLs by hand in
 * RootNavigator, which needs this parser. Origin-gated and whole-route exactly
 * like extractProjectSuggestionsDeepLink. Only a single `openUpload=1` opens the
 * sheet; a missing, malformed or repeated one still opens Files, rather than
 * dropping the link or guessing. Any other query parameter is ignored.
 */
export function extractProjectFilesDeepLink(
  url: string | null | undefined,
): { projectId: number; openUpload?: true } | null {
  if (!url || !isOwnAppUrl(url)) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  // In `com.eleplan.app://project/42/files` "project" is the host; in the
  // universal URL it is the first path segment.
  const path = parsed.protocol.toLowerCase() === 'com.eleplan.app:'
    ? (parsed.hostname.toLowerCase() === 'project' ? parsed.pathname.match(/^\/(\d+)\/files\/?$/i) : null)
    : parsed.pathname.match(/^\/project\/(\d+)\/files\/?$/i);
  const projectId = normalizeProjectId(path?.[1]);
  if (!projectId) return null;

  const openUpload = parsed.searchParams.getAll('openUpload');
  return openUpload.length === 1 && openUpload[0] === '1' ? { projectId, openUpload: true } : { projectId };
}

export function projectChatNavigationTarget(target: ProjectChatDeepLink): {
  screen: 'ProjectChat' | 'Conversation';
  params: ProjectChatDeepLink;
} {
  return target.conversationId
    ? {
        screen: 'Conversation',
        params: { projectId: target.projectId, conversationId: target.conversationId },
      }
    : { screen: 'ProjectChat', params: { projectId: target.projectId } };
}

export function shouldShowBrowserAppLinks(web: boolean, electron: boolean): boolean {
  return web && !electron;
}

export function shouldShowOpenInDesktopApp(
  web: boolean,
  electron: boolean,
  userAgent: string | null | undefined,
): boolean {
  if (!shouldShowBrowserAppLinks(web, electron)) return false;
  return !/(Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile)/i
    .test(String(userAgent || ''));
}

export function isDesktopHandoffPage(pathname: string | null | undefined): boolean {
  const normalized = String(pathname || '').replace(/\/+$/, '') || '/';
  return normalized === DESKTOP_HANDOFF_PATH;
}
